import React, { useState, useEffect, useCallback } from 'react';
import { userApiClient } from '../../api/HttpClient';
import AssignTenantDialog from './AssignTenantDialog';
import TenantPaymentsDialog from './TenantPaymentsDialog';
import TenantDetailsDialog from './TenantDetailsDialog';
import ChangeBedDialog from './ChangeBedDialog';
import { API_GET_LONGTERM_BEDS } from '../../api/config';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowLeft, Loader2 } from 'lucide-react';

interface BedsTableProps {
  roomId: string;
  roomNumber: string;
  propertyId: string;
  onClose: () => void;
}

interface Bed {
  id: string;
  bed_code: string;
  status: 'vacant' | 'occupied';
  tenant_name?: string;
  tenant_id?: string;
}

const BedsTable: React.FC<BedsTableProps> = ({ roomId, roomNumber, propertyId, onClose }) => {
  const [beds, setBeds] = useState<Bed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAssignTenantDialogOpen, setIsAssignTenantDialogOpen] = useState(false);
  const [selectedBedIdForAssignment, setSelectedBedIdForAssignment] = useState<string | null>(null);
  const [isTenantPaymentsDialogOpen, setIsTenantPaymentsDialogOpen] = useState(false);
  const [selectedTenantIdForPayments, setSelectedTenantIdForPayments] = useState<string | null>(null);
  const [isTenantDetailsDialogOpen, setIsTenantDetailsDialogOpen] = useState(false);
  const [selectedTenantIdForDetails, setSelectedTenantIdForDetails] = useState<string | null>(null);
  const [isChangeBedDialogOpen, setIsChangeBedDialogOpen] = useState(false);
  const [selectedBedForChange, setSelectedBedForChange] = useState<Bed | null>(null);

  const fetchBeds = useCallback(async () => {
    setLoading(true);
    try {
      const url = API_GET_LONGTERM_BEDS.replace('roomID', roomId);
      const client = await userApiClient;
      const response = await client.get<Bed[]>(url);
      setBeds(response);
    } catch (err: any) {
      console.error('Error fetching beds:', err);
      setError(err.response?.data?.detail || 'Failed to fetch beds.');
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    fetchBeds();
  }, [fetchBeds]);

  const handleAssignTenant = (bedId: string) => {
    setSelectedBedIdForAssignment(bedId);
    setIsAssignTenantDialogOpen(true);
  };

  const handleVacate = async (bedId: string) => {
    try {
      const client = await userApiClient;
      await client.patch(`/api/pg_tenant/longterm/beds/${bedId}/vacate/`);
      fetchBeds();
    } catch (err: any) {
      console.error('Error vacating bed:', err);
    }
  };

  const handleViewTenantDetails = (tenantId: string) => {
    setSelectedTenantIdForDetails(tenantId);
    setIsTenantDetailsDialogOpen(true);
  };

  const handleChangeBed = (bed: Bed) => {
    setSelectedBedForChange(bed);
    setIsChangeBedDialogOpen(true);
  };

  return (
    <div className="p-4 sm:p-6 bg-card text-card-foreground rounded-lg">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">Beds for Room {roomNumber}</h2>
          <p className="text-muted-foreground text-sm">Room ID: {roomId}</p>
        </div>
        <Button variant="outline" onClick={onClose}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Rooms
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="text-center text-destructive bg-destructive/10 p-4 rounded-md">
          <p className="font-medium">Error fetching beds</p>
          <p className="text-sm">{error}</p>
        </div>
      ) : beds.length === 0 ? (
        <div className="text-center text-muted-foreground py-12">
          <p>No beds found for this room.</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-semibold">Bed Code</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Tenant Name</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {beds.map((bed) => (
                <TableRow key={bed.id} className="hover:bg-muted/20">
                  <TableCell className="font-medium">{bed.bed_code}</TableCell>
                  <TableCell>
                    <Badge
                      variant={bed.status === 'occupied' ? 'destructive' : 'default'}
                      className={bed.status === 'occupied' ? 'bg-yellow-500/10 text-yellow-600' : 'bg-green-500/10 text-green-600'}
                    >
                      {bed.status.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{bed.tenant_name || 'N/A'}</TableCell>
                  <TableCell className="text-right">
                    {bed.status === 'vacant' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAssignTenant(bed.id)}
                      >
                        Assign Tenant
                      </Button>
                    ) : (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleVacate(bed.id)}
                        >
                          Vacate
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleChangeBed(bed)}
                        >
                          Change Bed
                        </Button>
                        {bed.tenant_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewTenantDetails(bed.tenant_id!)}
                          >
                            Tenant Details
                          </Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {selectedBedIdForAssignment && (
        <AssignTenantDialog
          open={isAssignTenantDialogOpen}
          onClose={() => setIsAssignTenantDialogOpen(false)}
          bedId={selectedBedIdForAssignment}
          onTenantAssigned={() => {
            setIsAssignTenantDialogOpen(false);
            fetchBeds();
          }}
        />
      )}

      {selectedTenantIdForPayments && (
        <TenantPaymentsDialog
          open={isTenantPaymentsDialogOpen}
          onClose={() => setIsTenantPaymentsDialogOpen(false)}
          tenantId={selectedTenantIdForPayments}
        />
      )}

      {selectedTenantIdForDetails && (
        <TenantDetailsDialog
          open={isTenantDetailsDialogOpen}
          onClose={() => setIsTenantDetailsDialogOpen(false)}
          tenantId={selectedTenantIdForDetails}
          propertyId={propertyId}
          roomNumber={roomNumber}
        />
      )}

      {selectedBedForChange && (
        <ChangeBedDialog
          open={isChangeBedDialogOpen}
          onClose={() => setIsChangeBedDialogOpen(false)}
          currentBedId={selectedBedForChange.id}
          currentBedCode={selectedBedForChange.bed_code}
          propertyId={propertyId}
          onBedChanged={() => {
            setIsChangeBedDialogOpen(false);
            fetchBeds();
          }}
        />
      )}
    </div>
  );
};

export default BedsTable;